import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { PRES, TYPE } from '../chrome';
import { COPY, originReviewHelpText, originReviewItemLabel } from '../presentationCopy';
import { OriginReview, OriginReviewItem, SlideObject } from '../types';

interface OriginReviewPanelProps {
  review: OriginReview;
  activeItem: OriginReviewItem | null;
  currentObject?: SlideObject | null;
  canEdit: boolean;
  busy?: boolean;
  onSelectItem: (item: OriginReviewItem) => void;
  onCopy: (item: OriginReviewItem) => void;
  onDismiss: (item: OriginReviewItem) => void;
  onOpenOriginal: (item: OriginReviewItem) => void;
}

function previewForObject(object?: SlideObject | null, kind?: OriginReviewItem['kind']): string {
  if (!object || !kind || kind === 'page') return '';
  if (kind === 'image') return object.imageUrl || '';
  if (kind === 'table') {
    return (object.table?.rows || [])
      .flatMap((row) => row.cells.map((cell) => String(cell.content || '').replace(/<[^>]+>/g, ' ').trim()))
      .filter(Boolean)
      .join(' ');
  }
  if (kind === 'chart') {
    const chart = object.chart;
    return [
      ...(chart?.categories || []),
      ...(chart?.series || []).flatMap((item) => [item.name, ...item.values.map((value) => String(value))]),
    ].filter(Boolean).join(' ');
  }
  return String(object.content || '');
}

const Preview: React.FC<{ kind: OriginReviewItem['kind']; value: string }> = ({ kind, value }) => {
  if (kind === 'page') {
    return (
      <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textPrimary }}>
        {value || '—'}
      </Typography>
    );
  }
  if (kind === 'image' && value) {
    return (
      <Box
        component="img"
        src={value}
        alt=""
        sx={{ width: '100%', maxHeight: 120, objectFit: 'contain', bgcolor: PRES.surfaceSunken, borderRadius: 1 }}
      />
    );
  }
  if (kind === 'text') {
    return (
      <Box
        sx={{
          fontFamily: TYPE.ui,
          ...TYPE.caption,
          color: PRES.textPrimary,
          maxHeight: 160,
          overflow: 'auto',
          '&, & *': { color: `${PRES.textPrimary} !important` },
          '& p': { m: 0 },
        }}
        dangerouslySetInnerHTML={{ __html: value || '—' }}
      />
    );
  }
  return (
    <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textPrimary, whiteSpace: 'pre-wrap' }}>
      {value || '—'}
    </Typography>
  );
};

const OriginReviewPanel: React.FC<OriginReviewPanelProps> = ({
  review,
  activeItem,
  currentObject,
  canEdit,
  busy,
  onSelectItem,
  onCopy,
  onDismiss,
  onOpenOriginal,
}) => {
  const item = activeItem || review.items[0] || null;
  if (!item) return null;
  const isPage = item.kind === 'page';
  const currentPreview = item.currentPreview || previewForObject(currentObject, item.kind);

  return (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        borderLeft: `1px solid ${PRES.borderSubtle}`,
        bgcolor: PRES.surface,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'auto',
        p: 1.5,
        gap: 1.25,
      }}
    >
      <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.title, color: PRES.textPrimary }}>
        {COPY.originReviewOriginal}
      </Typography>
      {review.items.length > 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {review.items.map((entry) => (
            <Button
              key={`${entry.slideId}-${entry.objectId}-${entry.kind}`}
              size="small"
              onClick={() => onSelectItem(entry)}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                color: PRES.textPrimary,
                bgcolor: entry.slideId === item.slideId && entry.objectId === item.objectId
                  ? PRES.surfaceSunken
                  : 'transparent',
              }}
            >
              {originReviewItemLabel(entry)}
            </Button>
          ))}
        </Box>
      )}
      {isPage ? (
        <Box>
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary, mb: 0.5 }}>
            {COPY.originReviewNewPage}
          </Typography>
          <Preview kind="page" value={item.originalPreview || ''} />
        </Box>
      ) : (
        <>
          <Box>
            <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary, mb: 0.5 }}>
              {COPY.originReviewOriginal}
            </Typography>
            <Preview kind={item.kind} value={item.originalPreview || ''} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary, mb: 0.5 }}>
              {COPY.originReviewCurrent}
            </Typography>
            <Preview kind={item.kind} value={currentPreview} />
          </Box>
        </>
      )}
      <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textSecondary }}>
        {originReviewHelpText(item.kind, item.libraryFollow)}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Button
          variant="contained"
          disabled={!canEdit || busy}
          onClick={() => onCopy(item)}
          sx={{ bgcolor: PRES.interactive, boxShadow: 'none', textTransform: 'none' }}
        >
          {isPage ? COPY.originReviewInsertPage : COPY.originReviewCopy}
        </Button>
        <Button disabled={!canEdit || busy} onClick={() => onDismiss(item)} sx={{ textTransform: 'none' }}>
          {COPY.originReviewDismiss}
        </Button>
        <Button onClick={() => onOpenOriginal(item)} sx={{ textTransform: 'none' }}>
          {COPY.originReviewOpenOriginal}
        </Button>
      </Box>
    </Box>
  );
};

export default OriginReviewPanel;
